// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useState } from 'react';
import { StyleSheet, View, type LayoutChangeEvent } from 'react-native';

import { Text } from '@/components/Themed';
import { useI18n } from '@/i18n';
import { statColors } from '@/features/statistics/components/StatPieces';
import type { BoardCurrentHole as CurrentHole, BoardRow } from '@/features/round-board/standings';
import appColors from '@/theme/colors';

/**
 * The hole being played, on the live board: its par and stroke index, and what each player has
 * taken on it so far.
 *
 * Replaces the eighteen-column grid the board used to draw. On a wall the whole card matters far
 * less than the hole in front of the group — and eighteen columns of two-digit numbers were
 * illegible from across a room anyway.
 *
 * The colour scale is the app's existing one (statColors); gold stays reserved for merits and
 * trophies, so a score never competes with the standings for the eye.
 *
 * Like the standings, it measures the space it was given and sizes its rows into it rather than
 * assuming they fit. It is handed the page of players the table is showing, so the two always name
 * the same people.
 */

type Props = {
  hole: CurrentHole;
  /** The page of players the standings are showing, so the panel never names somebody the table doesn't. */
  rows: Pick<BoardRow, 'playerId' | 'name' | 'currentHoleStrokes'>[];
  labelFont: number;
  /** The round is over: this is the last hole played, and nobody is still out on it. */
  closed?: boolean;
};

/** Below this a name stops being readable across a room; above it a two-ball becomes two bands. */
const MIN_ROW_HEIGHT = 18;
const MAX_ROW_HEIGHT = 48;

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

/** Colour a score against the hole's par. Unknown par means no opinion, so no colour. */
const strokeColor = (strokes: number, par: number | null): string => {
  if (par === null) {
    return appColors.text.primary;
  }
  const overPar = strokes - par;
  if (overPar <= -2) return statColors.eagle;
  if (overPar === -1) return statColors.birdie;
  if (overPar === 0) return statColors.par;
  if (overPar === 1) return statColors.bogey;
  return statColors.doublePlus;
};

export function BoardCurrentHole({ hole, rows, labelFont, closed = false }: Props) {
  const { t } = useI18n();

  // Measured rather than derived from the window: the strip is a fraction of a screen whose height
  // the panel does not know, and a panel that guesses wrong clips its own heading away.
  const [cardHeight, setCardHeight] = useState(0);
  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    setCardHeight(event.nativeEvent.layout.height);
  }, []);

  const available = Math.max(0, cardHeight - Math.round(labelFont * 1.4) - 6 - 20);
  const rowHeight = rows.length
    ? clamp(available / rows.length, MIN_ROW_HEIGHT, MAX_ROW_HEIGHT)
    : MAX_ROW_HEIGHT;
  const nameFont = clamp(Math.round(rowHeight * 0.5), 10, 26);
  const valueFont = Math.round(nameFont * 1.15);

  const heading = [
    t('roundBoard.currentHole', { hole: hole.holeNumber }),
    hole.par === null ? null : t('roundBoard.parIs', { par: hole.par }),
    hole.strokeIndex === null ? null : t('roundBoard.indexIs', { index: hole.strokeIndex }),
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <View style={styles.card} testID="board-current-hole" onLayout={handleLayout}>
      <Text
        testID="board-current-hole-heading"
        style={[styles.heading, { fontSize: labelFont }]}
        numberOfLines={1}
      >
        {heading}
      </Text>
      {rows.map((row) => (
        <View key={row.playerId} style={[styles.row, { height: rowHeight }]}>
          <Text style={[styles.name, { fontSize: nameFont }]} numberOfLines={1}>
            {row.name}
          </Text>
          {row.currentHoleStrokes === null ? (
            <Text
              testID={`board-hole-strokes-${row.playerId}`}
              style={[styles.playing, { fontSize: labelFont }]}
            >
              {closed ? '–' : t('roundBoard.playing')}
            </Text>
          ) : (
            <Text
              testID={`board-hole-strokes-${row.playerId}`}
              style={[
                styles.strokes,
                { fontSize: valueFont, color: strokeColor(row.currentHoleStrokes, hole.par) },
              ]}
            >
              {row.currentHoleStrokes}
            </Text>
          )}
        </View>
      ))}
    </View>
  );
}

export default BoardCurrentHole;

const styles = StyleSheet.create({
  card: {
    flex: 1,
    // The panel is handed a page that fits, but a clipped row still beats one spilling over the graph.
    overflow: 'hidden',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panel,
    paddingHorizontal: 14,
    paddingVertical: 10,
  },
  heading: {
    color: appColors.text.muted,
    textTransform: 'uppercase',
    letterSpacing: 1.2,
    fontWeight: '700',
    marginBottom: 6,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  name: {
    color: appColors.text.secondary,
    flexShrink: 1,
    paddingRight: 8,
  },
  strokes: {
    fontWeight: '800',
  },
  playing: {
    color: appColors.text.muted,
    fontStyle: 'italic',
  },
});
